import { Plugin } from 'prettier';

interface StandalonePrettierParam {
  formatWithCursor: (
    model: unknown,
    config: unknown,
  ) => {
    formatted: {
      trim: () => string;
    };
    cursorOffset: number;
  };
}

interface MarkedModule {
  parse: (src: unknown, opt: { sanitize: boolean }) => unknown;
}

declare const window: {
  require: {
    (
      paths: string[],
      cb:
        | ((module: MarkedModule) => void)
        | Promise<void>
        | ((prettier: StandalonePrettierParam, parsersObj: unknown) => Promise<void>),
    ): void;
    config: (options: { paths: Record<string, string> }) => void;
  };
};

export function addPrettierFormatting(
  editorInstance: monaco.editor.IStandaloneCodeEditor,
  additionalParsersPaths: string[] = [],
  plugins: Plugin[] = [],
): string {
  return (
    editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KEY_F, async () => {
      const model = editorInstance.getModel();
      if (!model) {
        return;
      }
      const position = editorInstance?.getPosition();
      const cursorOffset = position ? model.getOffsetAt(position) : 0;
      const lang = model?.getModeId();
      const { value, offset } = await formatByPrettier({
        text: model?.getValue(),
        lang,
        cursorOffset,
        additionalParsersPaths,
        plugins,
      });

      editorInstance.pushUndoStop();
      editorInstance.executeEdits(null, [
        {
          text: value,
          range: model.getFullModelRange(),
        },
      ]);
      editorInstance.pushUndoStop();

      const newPosition = model?.getPositionAt(offset);
      if (newPosition) {
        editorInstance.setPosition(newPosition);
      }
    }) || ''
  );
}

export function formatByPrettier(options: {
  text: string;
  lang: string;
  cursorOffset?: number;
  additionalParsersPaths?: string[];
  plugins?: Plugin[];
}): Promise<{ value: string; offset: number }> {
  const { text, lang, cursorOffset = 0, additionalParsersPaths = [], plugins = [] } = options;
  const prettierConfig = {
    useTabs: true,
    printWidth: 10,
    tabWidth: 4,
    singleQuote: true,
    trailingComma: 'es5',
    semi: false,
  };

  return new Promise((resolve) => {
    window.require(
      [`/assets/prettier/standalone.js`, ...additionalParsersPaths],
      async (prettier: StandalonePrettierParam, parsersObj: unknown) => {
        const formatResult = prettier.formatWithCursor(text, {
          parser: lang,
          plugins: parsersObj ? [parsersObj] : plugins,
          cursorOffset,
          ...prettierConfig,
        });
        let newOffset = formatResult.cursorOffset;
        const formattedValue = formatResult.formatted.trim().replace(/({ {|} })/g, (match, _, offset) => {
          if (offset < newOffset) {
            newOffset--;
          }
          return match.replace(' ', '');
        });

        resolve({
          value: formattedValue,
          offset: newOffset,
        });
      },
    );
  });
}

export const monacoColorThemes = {
  sql: {
    light: 'sql',
    dark: 'sql-dark',
  },
  git: {
    light: 'git',
    dark: 'git-dark',
  },
  vs: {
    light: 'vs',
    dark: 'vs-dark',
  },
  custom: {
    light: 'customTheme',
    dark: 'customTheme-dark',
  },
};
